import { useState, useCallback, useRef } from 'react'
import { DisplayStatusToolTip } from '../../../../../baseComponents/Displays/DisplayStatus'
import { RowData, TableData } from '../../../../../baseComponents/TwinTable/types'
import { CustomSelectColorDropDownWSearchBarBlockedItems } from '../../../../../forms/CustomSelect/CustomSelectColor'
import useOnClickOutside from '../../../../../utils/hooks/useOnClickOut'
import useTwinTranslation from '../../../../../utils/hooks/useTwinTranslation'
import twinFetchPost from '../../../../../utils/globals/data'
import { InputTableLayoutData } from '../../../../../baseComponents/TwinTable/InputTableLayout/types'
import { flushSync } from 'react-dom'
import { TwinDictionary } from '../../../../../utils/globals/dictionary'
import { WorkSheetProductStatusTypeInversed } from '@teinor/erp/types/company/order/sell/workSheet/workSheetProductStatus'
import { SetEditDeleteModalTy, UseEditDeleteModalProps } from '../../../../../utils/hooks/useEditDeleteModal'
import './customSelectWorkSheetProductStatus.sass'

interface CustomSelectWorkSheetProductStatusProps extends CustomSelectWorkSheetProductStatusLogicProps {
    WorkSheetProductStatusId: number
}

const CustomSelectWorkSheetProductStatus: React.FC<CustomSelectWorkSheetProductStatusProps> = ({ workSheetProductStatuses, WorkSheetProductStatusId, allRowData, ...rest }) => {
    const { t } = useTwinTranslation()
    const { changeWorkSheetProductStatus, opened, handleDropDown, ref } = useCustomSelectWorkSheetProductStatusLogic({ allRowData, workSheetProductStatuses, ...rest })
    const itemSelected = workSheetProductStatuses?.[WorkSheetProductStatusId]
    const message = itemSelected?.name || t('withoutStatus', 'Sin estado')
    const color = itemSelected?.color
    return (
        <div className='w-full custom_select_order_product_status' ref={ref}>
            <div onClick={handleDropDown} >
                <DisplayStatusToolTip text={message} colorText={color} className='w-full py-1 twin_elipsis' idTooltip={'workSheetProductStatus-' + allRowData?.id} />
            </div>
            {opened && <div className='absolute w-full bg-white'><CustomSelectColorDropDownWSearchBarBlockedItems items={workSheetProductStatuses} onChange={changeWorkSheetProductStatus} value={WorkSheetProductStatusId} /></div>}
        </div>
    )
}

interface CustomSelectWorkSheetProductStatusLogicProps {
    allRowData: RowData
    workSheetProductStatuses: TwinDictionary
    updateDataURL: string
    setCompleteData: (value: React.SetStateAction<InputTableLayoutData | null>) => void
    getCompleteData: (inputtable?: TableData) => Promise<void>
    setOpenModal: SetEditDeleteModalTy
}

const useCustomSelectWorkSheetProductStatusLogic = ({ allRowData, workSheetProductStatuses, updateDataURL, setCompleteData, getCompleteData, setOpenModal }: CustomSelectWorkSheetProductStatusLogicProps) => {
    const ref = useRef<any>(null)
    const [opened, setOpened] = useState<boolean | null>(null)

    const onChangeWorkSheetProductStatus = useCallback(async (rowIndex: number, value: string, typeStatus: string, LabelId?: number, SpecificPriceRuleForLabelId?: number) => {
        await flushSync(async () => {
            let extraParams: TwinDictionary = {}
            let extraParamsForTag: TwinDictionary = {}
            let productReference = ''
            setCompleteData((old) => {
                if (!old) {
                    return null
                }
                if (old?.tableData) {
                    if (old.tableData[rowIndex]['WorkSheetProductStatus.typeStatus'] === WorkSheetProductStatusTypeInversed['canceled'] && parseInt(typeStatus) !== WorkSheetProductStatusTypeInversed['canceled']) {
                        old.tableData[rowIndex]['quantity'] = old.tableData[rowIndex]['desiredQuantity']
                    }
                    old.tableData[rowIndex]['WorkSheetProductStatusId'] = value
                    old.tableData[rowIndex]['WorkSheetProductStatus.typeStatus'] = typeStatus
                }
                extraParams = { id: old.tableData[rowIndex].id, WorkSheetProductStatusId: value, typeStatus, WorkSheetId: old.header.id, quantity: old.tableData[rowIndex]['quantity'] }
                extraParamsForTag = { id: old.tableData[rowIndex].WorkSheetId, tagQuantity: old.tableData[rowIndex]['quantity'], LabelId, SpecificPriceRuleId: SpecificPriceRuleForLabelId, WorkSheetProductId: old.tableData[rowIndex].id }
                productReference = old.tableData[rowIndex].reference
                return { ...old }
            })
            const update = await twinFetchPost(updateDataURL, extraParams)
            if (update) {
                const responseJSON = await update.json()
                let openModal: UseEditDeleteModalProps = null
                if (responseJSON?.errors && responseJSON?.errors?.[0].msg.includes('matchProduct')) {
                    const errorSplit = responseJSON.errors[0].msg.split('-_-')
                    const WorkSheetProductMatchId = errorSplit[1]
                    const WorkSheetProductId = errorSplit[2]
                    const productNLine = errorSplit[3]
                    openModal = {
                        type: 'edit',
                        allRowData: { WorkSheetProductId: WorkSheetProductId, WorkSheetProductMatchId, productNLine, productReference }
                    }
                }
                if (LabelId) {
                    if (openModal?.allRowData) {
                        extraParamsForTag['extraParamsForMatch'] = openModal?.allRowData
                    }
                    openModal = { type: 'delete', allRowData: extraParamsForTag}
                }
                if (openModal) {
                    setOpenModal(openModal)
                }
                await getCompleteData()
            }
        })
    }, [setCompleteData, updateDataURL, getCompleteData, setOpenModal])

    const changeWorkSheetProductStatus = useCallback(async (value: any) => {
        await onChangeWorkSheetProductStatus(allRowData?.absoluteIndex || allRowData?.rowIndex, value, String(workSheetProductStatuses[parseInt(value)].typeStatus), workSheetProductStatuses[parseInt(value)].LabelId, workSheetProductStatuses[parseInt(value)].SpecificPriceRuleForLabelId)
        setOpened(null)
    }, [setOpened, allRowData, workSheetProductStatuses, onChangeWorkSheetProductStatus])

    const handleDropDown = useCallback(() => {
        setOpened((old) => {
            if (!old) {
                return true
            } else {
                return false
            }
        })
    }, [setOpened])

    useOnClickOutside(ref, () => setOpened(false))
    return { changeWorkSheetProductStatus, opened, setOpened, handleDropDown, ref }
}

export default CustomSelectWorkSheetProductStatus